import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatPercent, formatDate, getReturnColor } from '@/lib/utils'
import { MetricCard, LoadingSpinner } from '@/components/ui'
import { LineChart } from '@/components/charts'
import { Trash2, Check, AlertCircle } from 'lucide-react'

type Tab = 'overview' | 'analysis' | 'insert' | 'explorer'

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'analysis', label: 'Performance Analysis' },
  { id: 'insert', label: 'Insert Returns & NAV' },
  { id: 'explorer', label: 'Data Explorer' },
]

export default function HighFreqReturns() {
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">High-Frequency Returns</h1>
        <p className="text-muted-foreground">Intra-month MTD snapshots and TWR performance</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* TWR Methodology Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <span className="font-semibold">TWR = Time-Weighted Return</span> — combines intra-month (high-freq) snapshots
        and end-of-month official returns to produce a continuous return series free of cash-flow bias.
        The merged series (available via Performance Analysis) chains high-freq MTD estimates with
        official monthly returns for a complete picture.
      </div>

      {activeTab === 'overview' && <OverviewTab onSelectFund={(id) => { setSelectedFundId(id); setActiveTab('analysis') }} />}
      {activeTab === 'analysis' && <AnalysisTab selectedFundId={selectedFundId} onSelectFund={setSelectedFundId} />}
      {activeTab === 'insert' && <InsertTab />}
      {activeTab === 'explorer' && <ExplorerTab />}
    </div>
  )
}

/* ─── Overview Tab ─── */

function OverviewTab({ onSelectFund }: { onSelectFund: (id: number) => void }) {
  const { data: overview, isLoading } = useQuery({
    queryKey: ['high-freq-overview'],
    queryFn: () => api.get('/high-freq/overview').then((r) => r.data),
  })

  if (isLoading) return <LoadingSpinner />
  if (!overview?.length) return <Empty msg="No high-frequency data available" />

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold">High-Frequency Performance Overview</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fund</th>
              <th>Last Update</th>
              <th className="text-right">Latest MTD</th>
              <th className="text-right">YTD</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {overview.map((f: any) => (
              <tr key={f.fund_id} className="cursor-pointer hover:bg-accent/50" onClick={() => onSelectFund(f.fund_id)}>
                <td className="font-medium">{f.fund_name}</td>
                <td className="text-muted-foreground">{formatDate(f.last_update)}</td>
                <td className={`text-right font-mono ${getReturnColor(f.latest_mtd)}`}>
                  {f.latest_mtd != null ? formatPercent(f.latest_mtd) : '-'}
                </td>
                <td className={`text-right font-mono ${getReturnColor(f.ytd)}`}>
                  {f.ytd != null ? formatPercent(f.ytd) : '-'}
                </td>
                <td className="text-right">
                  <button className="text-xs px-2 py-1 border border-input rounded hover:bg-accent">
                    Analyze
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ─── Analysis Tab ─── */

function AnalysisTab({ selectedFundId, onSelectFund }: { selectedFundId: number | null; onSelectFund: (id: number) => void }) {
  // Load fund list
  const { data: funds } = useQuery({
    queryKey: ['high-freq-funds'],
    queryFn: () => api.get('/high-freq/funds').then((r) => r.data),
  })

  // Load returns for selected fund
  const { data: returns, isLoading } = useQuery({
    queryKey: ['high-freq-returns', selectedFundId],
    queryFn: () => api.get(`/high-freq/returns/${selectedFundId}?limit=100`).then((r) => r.data),
    enabled: !!selectedFundId,
  })

  // Load monthly returns
  const { data: monthly } = useQuery({
    queryKey: ['high-freq-monthly', selectedFundId],
    queryFn: () => api.get(`/high-freq/monthly/${selectedFundId}?limit=24`).then((r) => r.data),
    enabled: !!selectedFundId,
  })

  const latestMTD = returns?.[0]?.performance
  const fundName = funds?.find((f: any) => f.fund_id === selectedFundId)?.fund_name

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <label className="block text-sm font-medium text-muted-foreground mb-2">Select Fund</label>
        <select
          className="w-full max-w-md h-10 px-3 border border-input rounded-md bg-background text-sm"
          value={selectedFundId || ''}
          onChange={(e) => onSelectFund(parseInt(e.target.value) || 0)}
        >
          <option value="">Choose a fund...</option>
          {funds?.map((f: any) => (
            <option key={f.fund_id} value={f.fund_id}>{f.fund_name}</option>
          ))}
        </select>
      </div>

      {selectedFundId && isLoading && <LoadingSpinner />}

      {selectedFundId && returns && !isLoading && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard label="Current MTD" value={latestMTD != null ? formatPercent(latestMTD) : '-'} />
            <MetricCard label="Last Update" value={returns[0] ? formatDate(returns[0].date) : '-'} />
            <MetricCard label="Snapshots" value={returns.length} />
            <MetricCard label="Fund" value={fundName || '-'} />
          </div>

          {/* MTD Evolution Chart */}
          {returns.length > 1 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">MTD Evolution — {fundName}</h2>
              <LineChart
                data={[...returns].reverse().map((r: any) => ({
                  date: r.date,
                  mtd: r.performance,
                }))}
                xKey="date"
                lines={[{ key: 'mtd', name: 'MTD Performance', color: 'hsl(var(--primary))' }]}
                height={300}
                yTickFormatter={(v: number) => formatPercent(v)}
              />
            </div>
          )}

          {/* High-freq returns table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold">High-Frequency Snapshots</h2>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="text-right">MTD Return</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {returns.map((r: any) => (
                  <tr key={r.id}>
                    <td>{formatDate(r.date)}</td>
                    <td className={`text-right font-mono ${getReturnColor(r.performance)}`}>
                      {formatPercent(r.performance)}
                    </td>
                    <td className="text-muted-foreground text-sm">{r.note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Monthly returns */}
          {monthly && monthly.length > 0 && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold">Monthly Returns</h2>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-right">Return</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly.map((r: any) => (
                    <tr key={r.id}>
                      <td>{formatDate(r.date)}</td>
                      <td className={`text-right font-mono ${getReturnColor(r.performance)}`}>
                        {formatPercent(r.performance)}
                      </td>
                      <td className="text-muted-foreground">{r.performance_type_id === 1 ? 'Live' : r.performance_type_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* ─── Insert Tab ─── */

function InsertTab() {
  const queryClient = useQueryClient()
  const [fundId, setFundId] = useState<number | null>(null)
  const [snapshotDate, setSnapshotDate] = useState('')
  const [mtdReturn, setMtdReturn] = useState('')
  const [note, setNote] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Load all funds
  const { data: funds } = useQuery({
    queryKey: ['data-funds'],
    queryFn: () => api.get('/data/funds').then((r) => r.data),
  })

  // Load recent entries for selected fund
  const { data: recentEntries } = useQuery({
    queryKey: ['high-freq-returns', fundId],
    queryFn: () => api.get(`/high-freq/returns/${fundId}?limit=20`).then((r) => r.data),
    enabled: !!fundId,
  })

  // Insert mutation
  const insertMutation = useMutation({
    mutationFn: (payload: any) => api.post('/high-freq/returns', payload),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'High-frequency return inserted' })
      setMtdReturn('')
      setNote('')
      queryClient.invalidateQueries({ queryKey: ['high-freq-returns', fundId] })
      queryClient.invalidateQueries({ queryKey: ['high-freq-overview'] })
      setTimeout(() => setMessage(null), 3000)
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Insert failed' })
      setTimeout(() => setMessage(null), 5000)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (recordId: number) => api.delete(`/high-freq/returns/${recordId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['high-freq-returns', fundId] })
      queryClient.invalidateQueries({ queryKey: ['high-freq-overview'] })
    },
  })

  const handleInsert = () => {
    if (!fundId || !snapshotDate || !mtdReturn) return
    const perf = parseFloat(mtdReturn) / 100 // Convert percentage to decimal
    if (isNaN(perf)) return

    insertMutation.mutate({
      hedge_fund_id: fundId,
      date: snapshotDate,
      performance: perf,
      note: note || null,
    })
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {message.text}
        </div>
      )}

      {/* Insert form */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Insert High-Frequency Return</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-muted-foreground mb-1">Fund</label>
            <select
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              value={fundId || ''}
              onChange={(e) => setFundId(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Select a fund...</option>
              {funds?.map((f: any) => (
                <option key={f.fund_id} value={f.fund_id}>{f.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Snapshot Date</label>
            <input
              type="date"
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              value={snapshotDate}
              onChange={(e) => setSnapshotDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">MTD Return (%)</label>
            <input
              type="number"
              step="0.01"
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              placeholder="e.g. 2.5"
              value={mtdReturn}
              onChange={(e) => setMtdReturn(e.target.value)}
            />
          </div>
          <button
            onClick={handleInsert}
            disabled={!fundId || !snapshotDate || !mtdReturn || insertMutation.isPending}
            className="h-10 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {insertMutation.isPending ? 'Saving...' : 'Insert'}
          </button>
        </div>
        <div className="mt-3">
          <label className="block text-sm font-medium text-muted-foreground mb-1">Note (optional)</label>
          <input
            type="text"
            className="w-full max-w-md h-10 px-3 border border-input rounded-md bg-background text-sm"
            placeholder="Optional note..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>

      {/* Recent entries */}
      {fundId && recentEntries && recentEntries.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-semibold">Recent Entries</h2>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th className="text-right">MTD Return</th>
                <th>Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {recentEntries.map((r: any) => (
                <tr key={r.id}>
                  <td>{formatDate(r.date)}</td>
                  <td className={`text-right font-mono ${getReturnColor(r.performance)}`}>
                    {formatPercent(r.performance)}
                  </td>
                  <td className="text-muted-foreground text-sm">{r.note || ''}</td>
                  <td className="text-right">
                    <button
                      onClick={() => { if (confirm('Delete this entry?')) deleteMutation.mutate(r.id) }}
                      className="p-1.5 text-muted-foreground hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/* ─── Explorer Tab ─── */

function ExplorerTab() {
  const { data: funds, isLoading } = useQuery({
    queryKey: ['high-freq-funds'],
    queryFn: () => api.get('/high-freq/funds').then((r) => r.data),
  })

  const { data: latest } = useQuery({
    queryKey: ['high-freq-latest'],
    queryFn: () => api.get('/high-freq/latest').then((r) => r.data),
  })

  if (isLoading) return <LoadingSpinner />

  // Merge fund info with latest snapshot
  const merged = funds?.map((f: any) => {
    const snap = latest?.find((s: any) => s.fund_id === f.fund_id)
    return { ...f, latest_mtd: snap?.performance, last_date: snap?.date }
  })

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <MetricCard label="Funds with HF Data" value={funds?.length || 0} />
        <MetricCard label="Latest Snapshots" value={latest?.length || 0} />
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-semibold">All High-Frequency Funds</h2>
        </div>
        {!merged?.length ? (
          <div className="p-8 text-center text-muted-foreground">No funds with high-frequency data</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fund</th>
                  <th>Manager</th>
                  <th>Strategy</th>
                  <th className="text-right">Latest MTD</th>
                  <th>As of</th>
                </tr>
              </thead>
              <tbody>
                {merged.map((f: any) => (
                  <tr key={f.fund_id}>
                    <td className="font-medium">{f.fund_name}</td>
                    <td className="text-muted-foreground">{f.manager_name || '-'}</td>
                    <td className="text-muted-foreground">{f.main_strategy || '-'}</td>
                    <td className={`text-right font-mono ${getReturnColor(f.latest_mtd)}`}>
                      {f.latest_mtd != null ? formatPercent(f.latest_mtd) : '-'}
                    </td>
                    <td className="text-muted-foreground">{f.last_date ? formatDate(f.last_date) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── Helper ─── */

function Empty({ msg }: { msg: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
      {msg}
    </div>
  )
}
